/* eslint-disable @typescript-eslint/no-explicit-any */
// Loose `any` on normalize() row input + response generics: BE field-shape is
// not codegen-derived (migration plan: "Loose types over generated codegen types").
import instance, { ADMIN_PREFIX } from 'services/api';
import {
  Notification,
  NotificationInput,
  NotificationsListQuery,
  NotificationsListResponse,
  NotificationTemplateType
} from 'types/api/notifications';

/**
 * BE returns `trigger`/`action` as strings (bigint columns serialized by
 * the pg driver) and `created_at` is camelCase `createdAt` on the entity.
 * Normalize at the service boundary so the rest of the app keeps the
 * legacy numeric + snake_case shape the GQL fragment exposed.
 */
const normalize = (row: any): Notification => ({
  id: Number(row.id),
  type: row.type as NotificationTemplateType,
  trigger: Number(row.trigger ?? 0),
  action: Number(row.action ?? 0),
  title: (row.title ?? {}) as Notification['title'],
  body: (row.body ?? {}) as Notification['body'],
  data: row.data ?? null,
  fields: row.fields ?? null,
  keywords: row.keywords ?? null,
  template: row.template ?? null,
  created_at: row.created_at ?? row.createdAt ?? ''
});

export const fetchNotifications = async (
  query: NotificationsListQuery
): Promise<NotificationsListResponse> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.type !== undefined && query.type !== null && query.type !== '') {
    params.type = String(query.type);
  }

  if (
    query.trigger !== undefined &&
    query.trigger !== null &&
    query.trigger !== ''
  ) {
    params.trigger = String(query.trigger);
  }

  const res = await instance.get<{ results: any[]; total: number }>(
    `${ADMIN_PREFIX}/notifications`,
    { params }
  );

  return {
    results: (res.data.results ?? []).map(normalize),
    total: res.data.total ?? 0
  };
};

export const fetchNotificationById = async (
  id: string | number
): Promise<Notification> => {
  const res = await instance.get<any>(`${ADMIN_PREFIX}/notifications/id/${id}`);

  return normalize(res.data);
};

export const createNotification = (input: NotificationInput) =>
  instance.post<Notification>(`${ADMIN_PREFIX}/notifications`, input);

export const updateNotification = (
  id: string | number,
  input: NotificationInput
) => instance.put<Notification>(`${ADMIN_PREFIX}/notifications/${id}`, input);

export const deleteNotification = (id: string | number) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/notifications/${id}`);
